import { Spin } from 'antd';
import orderBy from 'lodash/orderBy';
import { useTranslation } from 'react-i18next';

import * as St from './SidePanelCookingAppliancesTab.styles';
import {
  isSourceInStore,
  isSourceProgram,
  setApplianceType,
} from './SidePanelCookingAppliancesTab.utils';

import { sortAttDesc } from '~/core/constants';
import { DesignSection, SubsectionButtonOption } from '~/core/enums';
import { useCookingAppliances } from '~/core/hooks/useCookingAppliances';
import { useGetDesignSectionTranslations } from '~/core/hooks/useGetDesignSectionTranslations';
import { ApplianceModel } from '~/core/models';
import {
  isSomething,
  sortAppliancesBy,
  updateListAndFilters,
} from '~/core/utils';
import {
  useCookingAppliancesContext,
  useDrawingShapeContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectBrowsedCookingAppliances,
  selectSelectedBrowsingOption,
  selectSuppliedCookingAppliances,
  updateSelectedCookingApplianceId,
} from '~/redux/slices/cookingAppliance';
import { resetActiveDrawingObject } from '~/redux/slices/drawingShape';
import {
  selectSelectedFilters,
  selectSortBy,
} from '~/redux/slices/sortAndFilter';
import ItemCountAdjustButton from '~atoms/ItemCountAdjustButton';
import DesignStepContainer from '~molecules/DesignStepContainer';
import CookingApplOptions from '~organisms/CookingApplOptions';
import DesignSectionCard from '~organisms/DesignSectionCard';
import DesignSectionHeader from '~organisms/DesignSectionHeader';
import RemoveAllSelectedProductsModal from '~organisms/RemoveAllSelectedProductsModal';
import RemoveApplianceModal from '~organisms/RemoveApplianceModal';

const SidePanelCookingApplianceTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const browsedCookingAppliances = useAppSelector(
    selectBrowsedCookingAppliances
  );
  const suppliedCookingAppliances = useAppSelector(
    selectSuppliedCookingAppliances
  );
  const selectedBrowsingOption = useAppSelector(selectSelectedBrowsingOption);

  const { loading } = useCookingAppliancesContext();

  const {
    getters: { activeDrawingAppliance },
  } = useDrawingShapeContext();

  const {
    decreaseQuantityHandler,
    increaseQuantityHandler,
    populateApplianceDisplayData,
    resetCookingAppliance,
    setShowRemoveApplianceModal,
    setApplianceToBeRemoved,
    showRemoveApplianceModal,
    applianceToBeRemoved,
    updateQuantityHandler,
  } = useCookingAppliances();

  const clearApplianceToBeRemoved = () => setApplianceToBeRemoved(undefined);

  // TODO: Refactor filters logic and redux state
  const sortBy = useAppSelector(selectSortBy('cooking'));
  const selectedFilters = useAppSelector(selectSelectedFilters('cooking'));

  const filterAppliances = (list: ApplianceModel[]) => {
    const { ids } = updateListAndFilters(list, selectedFilters);

    return ids
      .map((id) => list.find((ca) => ca.productId === id))
      .filter(isSomething);
  };

  const appliancesBy: Record<SubsectionButtonOption, ApplianceModel[]> = {
    [SubsectionButtonOption.BROWSE]: browsedCookingAppliances,
    [SubsectionButtonOption.SUPPLY]: suppliedCookingAppliances,
    [SubsectionButtonOption.NONE]: [],
  };
  const cookingAppliances = filterAppliances(
    appliancesBy[selectedBrowsingOption]
  ).map(setApplianceType);

  const cookingAppliancesCustomList = cookingAppliances.filter(
    (appliance) => isSourceProgram(appliance) || isSourceInStore(appliance)
  );

  const cookingAppliancesCustomListSorted = orderBy(
    sortAppliancesBy[sortBy](cookingAppliancesCustomList),
    [isSourceProgram, isSourceInStore],
    [sortAttDesc, sortAttDesc]
  );

  const noDragEndCallback = () => null;

  const highlightSelectedAppliance = (cookingAppliance: ApplianceModel) =>
    dispatch(updateSelectedCookingApplianceId(cookingAppliance.productId));

  const getApplianceCards = (appliances: ApplianceModel[]) =>
    appliances
      .map(populateApplianceDisplayData)
      .map(({ appliance, title, image, qty, isSelected }) => (
        <DesignSectionCard
          key={`cooking-appliance-${appliance.productId}`}
          title={title}
          image={image}
          isSelected={isSelected}
          tooltipText={t('DESIGN_TOOLBAR.DRAG_ONTO_DESIGN')}
          onMouseDown={() => highlightSelectedAppliance(appliance)}
          dndArgs={{
            appliance,
            onDragEnd: noDragEndCallback,
          }}
        >
          <ItemCountAdjustButton
            quantity={qty || undefined}
            onIncrease={increaseQuantityHandler(appliance)}
            onDecrease={
              qty > 0 ? decreaseQuantityHandler(appliance) : undefined
            }
            onUpdateValue={updateQuantityHandler(appliance)}
          />
        </DesignSectionCard>
      ));

  const {
    title,
    description,
    selectMessage,
    noCookingApplianceAvailable,
    modalRemoveAllCookingAppliancesDescription,
    modalNoCookingApplianceInSpaceTitle,
  } = useGetDesignSectionTranslations(DesignSection.COOKING_APPLIANCES, [
    'SELECT_MESSAGE',
    'NO_COOKING_APPLIANCE_AVAILABLE',
    'MODAL.REMOVE_ALL_COOKING_APPLIANCES.DESCRIPTION',
    'MODAL.NO_COOKING_APPLIANCE_IN_SPACE.TITLE',
  ]);

  return (
    <>
      <DesignSectionHeader title={title} description={description} />
      <CookingApplOptions
        selectedCookingApplianceSectionOption={selectedBrowsingOption}
      />

      {loading ? (
        <Spin style={{ fontSize: 24, marginLeft: '40%' }} />
      ) : (
        <>
          {selectedBrowsingOption === SubsectionButtonOption.SUPPLY && (
            <St.SectionTitle>{selectMessage}</St.SectionTitle>
          )}

          <DesignStepContainer>
            {selectedBrowsingOption === SubsectionButtonOption.BROWSE &&
              !cookingAppliancesCustomListSorted.length && (
                <span>{noCookingApplianceAvailable}</span>
              )}

            {getApplianceCards(
              selectedBrowsingOption === SubsectionButtonOption.BROWSE
                ? cookingAppliancesCustomListSorted
                : sortAppliancesBy[sortBy](cookingAppliances)
            )}
          </DesignStepContainer>
        </>
      )}

      <RemoveAllSelectedProductsModal
        description={modalRemoveAllCookingAppliancesDescription}
        isOpen={!!applianceToBeRemoved}
        title={modalNoCookingApplianceInSpaceTitle}
        onClose={clearApplianceToBeRemoved}
        onConfirm={() => {
          if (
            applianceToBeRemoved &&
            applianceToBeRemoved.id ===
              activeDrawingAppliance?.applianceModel.id
          ) {
            resetCookingAppliance(applianceToBeRemoved);
            clearApplianceToBeRemoved();
            dispatch(resetActiveDrawingObject());
          }
        }}
      />

      <RemoveApplianceModal
        title={t('DESIGN_TOOLBAR.REMOVE_MODAL.TITLE', {
          option: t('COMMON.APPLIANCE'),
        })}
        description={t('DESIGN_TOOLBAR.REMOVE_MODAL.APPLIANCE')}
        isOpen={showRemoveApplianceModal}
        onClose={() => setShowRemoveApplianceModal(false)}
      />
    </>
  );
};

export default SidePanelCookingApplianceTab;
